import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { PMPackagePartsTable, PMPackageReadyBadge } from './PMPackagesPanel';

// Plant-wide searchable PM Package readiness view for the Inventory page.
const PMPackagesList = () => {
  const [packages, setPackages] = useState([]);
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/pm-packages`);
        setPackages(res.data || []);
      } catch { /* non-fatal */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? packages.filter((p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.equipment_name || '').toLowerCase().includes(q) ||
        (p.parts || []).some((pt) =>
          (pt.part_number || '').toLowerCase().includes(q) ||
          (pt.name || '').toLowerCase().includes(q)
        )
      )
    : packages;

  return (
    <div className="space-y-4" data-testid="pm-packages-list">
      <Input
        placeholder="Search by package name, equipment, or part number…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
        data-testid="pm-packages-search"
      />

      {loading ? (
        <div className="text-slate-500 text-sm py-8 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-500 text-sm py-8 text-center" data-testid="pm-packages-empty">
          {packages.length === 0
            ? 'No PM packages yet. Create them from any equipment page (PM Packages card).'
            : 'No packages match your search.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((pkg) => (
            <Card key={pkg.id} data-testid={`pm-packages-list-item-${pkg.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    onClick={() => toggleExpand(pkg.id)}
                    data-testid={`pm-packages-list-expand-${pkg.id}`}
                  >
                    <span className="text-slate-400 text-xs w-3">{expandedIds.has(pkg.id) ? '▾' : '▸'}</span>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{pkg.name}</div>
                      <div className="text-xs text-slate-500 truncate">{pkg.equipment_name}</div>
                    </div>
                  </button>
                  <PMPackageReadyBadge pkg={pkg} />
                  <span className="text-xs text-slate-400">{pkg.parts.length} part{pkg.parts.length === 1 ? '' : 's'}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/machinery/${pkg.machinery_id}`)}
                    data-testid={`pm-packages-list-view-equipment-${pkg.id}`}
                  >
                    View equipment →
                  </Button>
                </div>
                {expandedIds.has(pkg.id) && (
                  <div className="mt-3">
                    <PMPackagePartsTable parts={pkg.parts} testPrefix={`pm-list-${pkg.id}`} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PMPackagesList;
